import { forwardRef, useImperativeHandle } from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { Radio } from 'ui/radio';
import { Input } from 'ui/input';
import { Select } from 'ui/select';
import { Checkbox } from 'ui/checkbox';
import { TextArea } from 'ui/text-area';
import { RadioGroup } from 'ui/radio-group';

import { formatDate } from 'helpers/dates-formatter';

import {
  AID_MODEL_DESCRIPTION_MAX_LENGTH,
  AID_MODEL_NAME_MAX_LENGTH,
  DATASET_DATE_FORMAT,
  DATASET_STATUSES,
  DEFAULT_ENTRY_TERM,
  ENROLLMENT_TYPES,
} from '../../aid-studio-details.constants';

interface SourceTableOption {
  value: string;
  label: string;
}

interface DatasetData {
  status?: string;
  datasetTableCreatedDate?: string;
  lastRecreatedDate?: string;
  sourceTableName?: string;
}

interface BasicInfoStepProps {
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  sourceTablesOptions: SourceTableOption[];
  sourceTablesLoading: boolean;
  sourceTablesFetching: boolean;
  isDirty: boolean;
  isValid: boolean;
  onSave: () => void;
  isNewModel: boolean;
  recreateDataset: boolean;
  onRecreateDatasetChange: (checked: boolean) => void;
  datasetData?: DatasetData;
  isUserView?: boolean;
  readOnly: boolean;
}

const BasicInfoStep = forwardRef<unknown, BasicInfoStepProps>(
  (
    {
      control,
      errors,
      sourceTablesOptions,
      sourceTablesLoading,
      sourceTablesFetching,
      isDirty,
      isValid,
      onSave,
      isNewModel,
      recreateDataset,
      onRecreateDatasetChange,
      datasetData,
      isUserView,
      readOnly,
    },
    ref
  ) => {
    useImperativeHandle(
      ref,
      () => ({
        hasChanges: (isDirty || recreateDataset) && isValid,
        save: onSave,
      }),
      [isDirty, isValid, onSave, recreateDataset]
    );

    const isDatasetInProgress =
      datasetData?.status === DATASET_STATUSES.IN_PROGRESS;
    const showDatasetFields = !isUserView;

    return (
      <Stack
        direction="row"
        sx={{ position: 'relative', gap: 4 }}
        data-testid="basic-info-step"
        inert={readOnly}
      >
        <Stack sx={{ width: '50%', gap: 2 }}>
          {isNewModel && (
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Model name is required',
                maxLength: {
                  value: AID_MODEL_NAME_MAX_LENGTH,
                  message: `Model name cannot exceed ${AID_MODEL_NAME_MAX_LENGTH} characters`,
                },
              }}
              render={({ field }) => (
                <Input
                  label="Model name"
                  id="name"
                  required
                  error={!!errors.name}
                  errorMessage={errors.name?.message}
                  placeholder="Enter model name"
                  data-testid="model-name-input"
                  {...field}
                  value={(field.value as string | undefined) ?? ''}
                />
              )}
            />
          )}

          <Stack direction="row" sx={{ gap: 3 }}>
            <Box sx={{ width: '50%' }}>
              <Controller
                name="entryTerm"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Entry term year"
                    id="entryTerm"
                    disabled
                    error={!!errors.entryTerm}
                    errorMessage={
                      errors.entryTerm?.message as string | undefined
                    }
                    options={DEFAULT_ENTRY_TERM}
                    data-testid="entry-term-select"
                    {...field}
                    value={DEFAULT_ENTRY_TERM.find(
                      (opt) => opt.value === field.value
                    )}
                    onChange={(option) => {
                      if (option && !Array.isArray(option)) {
                        field.onChange(option.value);
                      }
                    }}
                  />
                )}
              />
            </Box>

            <Box sx={{ width: '50%' }}>
              <span
                id="enrollment-type-label"
                className="font-semibold flex mb-2"
              >
                Enrollment type
              </span>
              <Controller
                name="enrollmentType"
                control={control}
                rules={{ required: 'Enrollment type is required' }}
                render={({ field }) => (
                  <RadioGroup
                    aria-labelledby="enrollment-type-label"
                    required
                    row
                    name={field.name}
                    value={(field.value as string | undefined) ?? ''}
                    onChange={(_event, value) => field.onChange(value)}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    error={!!errors.enrollmentType}
                    helperText={errors.enrollmentType?.message}
                    sx={{ pt: 0.5 }}
                  >
                    {ENROLLMENT_TYPES.map(({ value, label }) => (
                      <Radio
                        key={value}
                        value={value}
                        label={label}
                        id={`enrollment-${value}`}
                        data-testid={`enrollment-type-${value}`}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </Box>
          </Stack>

          {showDatasetFields && (
            <Box>
              <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-end' }}>
                <Box sx={{ flex: '1 1 0%' }}>
                  <Controller
                    name="sourceTableName"
                    control={control}
                    rules={{ required: 'Data science table is required' }}
                    render={({ field }) => (
                      <Select<SourceTableOption>
                        label="Data science table"
                        id="sourceTableName"
                        required
                        disabled={
                          sourceTablesLoading ||
                          (!isNewModel && !recreateDataset)
                        }
                        error={!!errors.sourceTableName}
                        errorMessage={
                          errors.sourceTableName?.message as string | undefined
                        }
                        options={sourceTablesOptions}
                        loading={sourceTablesLoading || sourceTablesFetching}
                        placeholder={
                          sourceTablesLoading ? 'Loading...' : 'Select table'
                        }
                        data-testid="source-table-select"
                        {...field}
                        value={sourceTablesOptions?.find(
                          (opt) => opt.value === field.value
                        )}
                        onChange={(option) => {
                          if (option && !Array.isArray(option)) {
                            field.onChange(option.value);
                          }
                        }}
                      />
                    )}
                  />
                </Box>

                {!isNewModel && (
                  <Checkbox
                    id="recreate-dataset"
                    label="Recreate dataset"
                    checked={recreateDataset}
                    onChange={(_event, isChecked) =>
                      onRecreateDatasetChange(isChecked)
                    }
                    disabled={isDatasetInProgress}
                    sx={{ pb: 1 }}
                    data-testid="checkbox-recreate-dataset"
                  />
                )}
              </Stack>

              {!isNewModel && datasetData?.lastRecreatedDate && (
                <p
                  className="text-xs font-semibold text-gray-700 mt-1"
                  data-testid="last-recreated-date"
                >
                  Last recreated:{' '}
                  {formatDate(
                    datasetData.lastRecreatedDate,
                    DATASET_DATE_FORMAT
                  )}
                </p>
              )}
            </Box>
          )}
        </Stack>

        <Stack sx={{ width: '50%' }}>
          <Controller
            name="description"
            control={control}
            rules={{
              maxLength: {
                value: AID_MODEL_DESCRIPTION_MAX_LENGTH,
                message: `Description cannot exceed ${AID_MODEL_DESCRIPTION_MAX_LENGTH} characters`,
              },
            }}
            render={({ field }) => (
              <TextArea
                label="Description (optional)"
                id="description"
                maxLength={AID_MODEL_DESCRIPTION_MAX_LENGTH}
                rows={8}
                showCharCount={false}
                placeholder="Enter model description"
                error={!!errors.description}
                errorMessage={errors.description?.message}
                data-testid="model-description-textarea"
                {...field}
                value={(field.value as string | undefined) ?? ''}
              />
            )}
          />
        </Stack>
      </Stack>
    );
  }
);

BasicInfoStep.displayName = 'BasicInfoStep';

export default BasicInfoStep;
